/**
 * 의약품 부가정보 편집 모달 — 레거시 `EditDrugAdditionalInfo.js` 간소화 포팅
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

export type DrugAdditionalField = 'direction' | 'effect' | 'precaution';

export interface DrugAdditionalSection {
  sectionTitle?: string;
  articles?: Array<{
    articleTitle?: string;
    paragraphs?: string[];
  }>;
}

const FIELD_LABEL: Record<DrugAdditionalField, string> = {
  direction: '용법',
  effect: '효능·효과',
  precaution: '사용상 주의',
};

interface DrugAdditionalInfoModalProps {
  open: boolean;
  field: DrugAdditionalField;
  sections: DrugAdditionalSection[];
  onClose: () => void;
  onSave: (sections: DrugAdditionalSection[]) => void;
}

const emptySection = (): DrugAdditionalSection => ({
  sectionTitle: '',
  articles: [{ articleTitle: '', paragraphs: [''] }],
});

export const DrugAdditionalInfoModal: React.FC<DrugAdditionalInfoModalProps> = ({
  open,
  field,
  sections,
  onClose,
  onSave,
}) => {
  const [rows, setRows] = useState<DrugAdditionalSection[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(sections.length > 0 ? structuredClone(sections) : [emptySection()]);
    }
  }, [open, sections]);

  const handleSave = () => {
    onSave(rows);
    setConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{FIELD_LABEL[field]} 부가정보 편집</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {rows.map((section, sectionIndex) => (
              <div key={`section-${sectionIndex}`} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={section.sectionTitle ?? ''}
                    placeholder="섹션 제목"
                    onChange={(e) => {
                      const value = e.target.value;
                      setRows((prev) =>
                        prev.map((item, i) =>
                          i === sectionIndex
                            ? { ...item, sectionTitle: value }
                            : item,
                        ),
                      );
                    }}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      setRows((prev) => prev.filter((_, i) => i !== sectionIndex))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {(section.articles ?? []).map((article, articleIndex) => (
                  <div
                    key={`article-${sectionIndex}-${articleIndex}`}
                    className="ml-2 space-y-2 border-l pl-3"
                  >
                    <Input
                      value={article.articleTitle ?? ''}
                      placeholder="항목 제목"
                      onChange={(e) => {
                        const value = e.target.value;
                        setRows((prev) =>
                          prev.map((item, i) => {
                            if (i !== sectionIndex) return item;
                            const articles = [...(item.articles ?? [])];
                            articles[articleIndex] = {
                              ...articles[articleIndex],
                              articleTitle: value,
                            };
                            return { ...item, articles };
                          }),
                        );
                      }}
                    />
                    {(article.paragraphs ?? ['']).map((paragraph, paragraphIndex) => (
                      <textarea
                        key={`p-${paragraphIndex}`}
                        rows={2}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={paragraph}
                        placeholder="문단"
                        onChange={(e) => {
                          const value = e.target.value;
                          setRows((prev) =>
                            prev.map((item, i) => {
                              if (i !== sectionIndex) return item;
                              const articles = [...(item.articles ?? [])];
                              const paragraphs = [
                                ...(articles[articleIndex]?.paragraphs ?? []),
                              ];
                              paragraphs[paragraphIndex] = value;
                              articles[articleIndex] = {
                                ...articles[articleIndex],
                                paragraphs,
                              };
                              return { ...item, articles };
                            }),
                          );
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRows((prev) => [...prev, emptySection()])}
            >
              <Plus className="mr-1 h-3 w-3" />
              섹션 추가
            </Button>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="button" onClick={() => setConfirmOpen(true)}>
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장 확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 부가정보를 저장합니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>저장</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/** 섹션 구조를 화면용 텍스트로 변환 */
export const formatAdditionalSections = (
  sections: DrugAdditionalSection[],
): string =>
  sections
    .map((section) => {
      const title = section.sectionTitle?.trim() ?? '';
      const articles = (section.articles ?? [])
        .map((article) => {
          const articleTitle = article.articleTitle?.trim() ?? '';
          const paragraphs = (article.paragraphs ?? [])
            .map((p) => p.trim())
            .filter(Boolean)
            .join('\n');
          return [articleTitle, paragraphs].filter(Boolean).join('\n');
        })
        .filter(Boolean)
        .join('\n\n');
      return [title, articles].filter(Boolean).join('\n');
    })
    .filter(Boolean)
    .join('\n\n');
